import { Box, Typography, Chip, alpha, useTheme } from "@mui/material";
import { ACCENT } from "./accent";

const formatDuration = (s) => {
  if (s == null) return "—";
  const total = Math.round(s);
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

const StatTile = ({ label, value, unit, tone = "neutral" }) => {
  const theme = useTheme();
  const toneColor =
    tone === "warn"
      ? theme.palette.warning.main
      : tone === "good"
        ? theme.palette.success.main
        : theme.palette.text.primary;
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.12 : 0.9),
        minWidth: 0,
      }}
    >
      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 700, color: toneColor, wordBreak: "break-word" }}>
        {value}
        {unit && (
          <Typography component="span" variant="body2" sx={{ ml: 0.5, color: theme.palette.text.secondary }}>
            {unit}
          </Typography>
        )}
      </Typography>
    </Box>
  );
};

/**
 * Stat tiles over the parsed summary jsonb. Health tone is applied ONLY to
 * unambiguous facts (lost 3D GPS fix; accelerometer clipping). Everything else
 * is shown neutrally — interpretation is the assistant's job, not this grid's.
 */
const LogSummary = ({ log }) => {
  const s = log?.summary || {};

  const tiles = [];
  tiles.push({ label: "Duration", value: formatDuration(s.duration_s) });
  if (log?.vehicle_type) tiles.push({ label: "Vehicle", value: log.vehicle_type });

  if (s.gps) {
    if (s.gps.sats_min != null) tiles.push({ label: "Min satellites", value: s.gps.sats_min });
    if (s.gps.hdop_max != null) tiles.push({ label: "Max HDOP", value: s.gps.hdop_max });
    if (s.gps.fix_min != null && s.gps.fix_min < 3)
      tiles.push({ label: "GPS fix", value: "Lost 3D", tone: "warn" });
  }
  if (s.battery) {
    if (s.battery.volt_min != null) tiles.push({ label: "Min voltage", value: s.battery.volt_min, unit: "V" });
    if (s.battery.consumed_mah != null) tiles.push({ label: "Consumed", value: s.battery.consumed_mah, unit: "mAh" });
  }
  if (s.vibration) {
    const peak = Math.max(
      s.vibration.vibex_max ?? 0,
      s.vibration.vibey_max ?? 0,
      s.vibration.vibez_max ?? 0
    );
    if (peak > 0) tiles.push({ label: "Peak vibration", value: peak.toFixed(1) });
    if (s.vibration.clip_max > 0)
      tiles.push({ label: "Accel clipping", value: s.vibration.clip_max, tone: "warn" });
  }
  if (s.attitude?.roll_err_max_deg != null)
    tiles.push({ label: "Max roll error", value: s.attitude.roll_err_max_deg, unit: "°" });

  return (
    <Box>
      {s.firmware && (
        <Chip
          label={s.firmware}
          size="small"
          sx={{
            mb: 2,
            fontFamily: "monospace",
            backgroundColor: alpha(ACCENT, 0.12),
            color: ACCENT,
          }}
        />
      )}
      {s.truncated && (
        <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
          Note: the log appears truncated — it may have ended mid-flight.
        </Typography>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
          gap: 1.5,
        }}
      >
        {tiles.map((t) => (
          <StatTile key={t.label} {...t} />
        ))}
      </Box>
    </Box>
  );
};

export default LogSummary;
